import cx from 'classnames';
import { ChartMetaFormField } from 'models/ui';
import { NAV_ID } from 'constants/app.constant';
import { Box, ChartPermission, ErrorCard, ErrorFormField, SectionDivider } from 'components/common';
import { ELSButton, ELSFlex, ELSFlexItem, ELSIcon } from 'components/common/els';
import { ChartViewProps } from 'components/features/shared/constants';
import {
  BLOCK,
  CONSULTATION_REQUEST_NOTICE,
  FIELD_CONFIG_MAP,
  FieldConfig,
  FormField,
  LAST_STEP_INDEX,
  PATIENT_INFORMATION_QUESTIONS,
  PRODUCT_REQUEST_NOTICE,
  PRODUCT_REQUEST_TABLE_HEADERS,
  RATIONALE_SECTIONS,
  STEP_CONFIGS,
  SectionTitle,
  TRANSFUSION_ORDER_HINT,
  TRANSFUSION_REACTION_INTRO,
  TRANSFUSION_REACTION_STEPS,
  TRANSFUSION_REACTION_YES,
  VERIFICATION_INTRO,
  VERIFICATION_STEPS,
  VITAL_SIGNS_TABLE_HEADERS,
  ViewTitle,
  getPlaceholderClass
} from './constants';
import ProductRequestSidebar, { ProductRequestRow } from './ProductRequestSidebar';
import TransfusionDetailsSidebar, { TransfusionDetailEntry } from './TransfusionDetailsSidebar';
import './blood.product.administration.record.scss';

export interface BloodProductAdministrationRecordViewProps extends ChartViewProps {
  activeStep: number;
  canContinue: boolean;
  canReset: boolean;
  completedSteps: boolean[];
  displayAuthoringData?: Function;
  enableDisplayRecordsButton?: boolean;
  isAuthor: boolean;
  maxSelectableStep: number;
  isFinalStepReady: boolean;
  isProductRequestSidebarOpen: boolean;
  isTransfusionSidebarOpen: boolean;
  productRequestResetCount: number;
  productRequests: ProductRequestRow[];
  transfusionDetails: TransfusionDetailEntry[];
  transfusionResetCount: number;
  onAddProductRequests: (rows: ProductRequestRow[]) => void;
  onAddTransfusionDetail: (entry: TransfusionDetailEntry | null) => void;
  onBackStep: VoidFunction;
  onCloseProductRequestSidebar: VoidFunction;
  onCloseTransfusionSidebar: VoidFunction;
  onContinueStep: VoidFunction;
  onOpenProductRequestSidebar: VoidFunction;
  onOpenTransfusionSidebar: VoidFunction;
  onResetStep: VoidFunction;
  onSaveClick: VoidFunction;
  onSelectStep: (stepIndex: number) => void;
}

const getFieldOptions = (fieldConfig?: FieldConfig) => (fieldConfig?.displayHorizontal ? { displayHorizontal: true } : undefined);

// Lays the "Other" free-text box beside its option instead of on a line of its own.
const getFieldInputProps = (fieldConfig?: FieldConfig) =>
  fieldConfig?.nestedOtherLayout ? { containerClassName: cx(`${BLOCK}__options`, { [`${BLOCK}__options--inline`]: fieldConfig.nestedOtherLayout === 'inline' }) } : undefined;

// The nested "Other" free-text box belongs on screen only while its option is selected.
const getNestedOtherFieldMap = (formField: ChartMetaFormField | undefined, formFieldMap: Map<string, ChartMetaFormField>) => {
  const otherOption = formField?.chartContent?.find((item) => item.nestedFieldId);
  const nestedField = otherOption && formFieldMap.get(otherOption.nestedFieldId);
  if (!nestedField) return formFieldMap;

  return new Map(formFieldMap).set(otherOption.nestedFieldId, { ...nestedField, hide: !otherOption.selected });
};

// The asterisk marks the field as mandatory, so it persists regardless of value - filling a
// mandatory field never removes it. Mirrors withPersistentRequired in AnnualAmbulatoryCareView.
const withPersistentRequired = (formField: ChartMetaFormField | undefined, isRequired: boolean): ChartMetaFormField | undefined => {
  if (!formField) return formField;
  return { ...formField, isRequired };
};

const BloodProductAdministrationRecordView = (props: BloodProductAdministrationRecordViewProps) => {
  const {
    activeStep,
    canContinue,
    canReset,
    completedSteps,
    displayAuthoringData,
    enableDisplayRecordsButton,
    formFieldMap,
    formSubmittedCount,
    isAuthor,
    maxSelectableStep,
    isFinalStepReady,
    isProductRequestSidebarOpen,
    isTransfusionSidebarOpen,
    productRequestResetCount,
    productRequests,
    transfusionDetails,
    transfusionResetCount,
    onAddProductRequests,
    onAddTransfusionDetail,
    onBackStep,
    onCloseProductRequestSidebar,
    onCloseTransfusionSidebar,
    onContinueStep,
    onOpenProductRequestSidebar,
    onOpenTransfusionSidebar,
    onResetStep,
    onSaveClick,
    onSelectStep
  } = props;

  const renderField = (fieldName: string, width = '1o3') => {
    const formField = formFieldMap.get(fieldName);

    return (
      <ELSFlexItem key={fieldName} w={width} md="1o1">
        <div className={getPlaceholderClass(formField)}>
          <ErrorFormField
            className={`${BLOCK}__field`}
            formField={withPersistentRequired(formField, Boolean(FIELD_CONFIG_MAP.get(fieldName)?.isRequired))}
            formFieldMap={getNestedOtherFieldMap(formField, formFieldMap)}
            formSubmittedCount={formSubmittedCount}
            inputProps={getFieldInputProps(FIELD_CONFIG_MAP.get(fieldName))}
            options={getFieldOptions(FIELD_CONFIG_MAP.get(fieldName))}
          />
        </div>
      </ELSFlexItem>
    );
  };

  const renderFieldRow = (fieldNames: string[], width = '1o3') => (
    <ELSFlex key={fieldNames.join('-')} left wrap className={`${BLOCK}__row`}>
      {fieldNames.map((fieldName) => renderField(fieldName, width))}
    </ELSFlex>
  );

  const renderOrderedList = (items: string[]) => (
    <ol className={`${BLOCK}__ordered-list`}>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  );

  const renderAddButton = (text: string, onClick: VoidFunction) => (
    <ELSButton className={`${BLOCK}__add-button`} type="secondary" text={text} iconName="add-circle" iconPosition="right" iconSize="1x" onClick={onClick} />
  );

  const renderEditButton = (label: string, onClick: VoidFunction) => (
    <ELSButton className={`${BLOCK}__edit-button`} type="link" text="" iconName="edit" iconSize="1x" ariaLabel={label} onClick={onClick} />
  );

  const renderConsultationRequest = () => (
    <>
      <p className={`${BLOCK}__notice`}>{CONSULTATION_REQUEST_NOTICE}</p>
      {renderFieldRow([FormField.DAY_COLLECTED, FormField.COLLECTED_BY, FormField.TIME_COLLECTED])}
      <SectionDivider mt />
      {renderFieldRow([FormField.CONSULTATION_DATE, FormField.PATIENT_LOCATION, FormField.DIAGNOSIS])}
      {renderFieldRow([FormField.ORDERING_PROVIDER_FIRST_NAME, FormField.ORDERING_PROVIDER_LAST_NAME, FormField.PROVIDER_SIGNATURE])}
      {renderFieldRow([FormField.TELEPHONE_PAGER_NUMBER])}
    </>
  );

  const renderRationaleForTransfusion = () => (
    <>
      {RATIONALE_SECTIONS.map((section, sectionIndex) => (
        <div className={cx(`${BLOCK}__section`, { [`${BLOCK}__section--divided`]: sectionIndex > 0 })} key={section.name}>
          <h4 className={`${BLOCK}__section-title`}>{section.title}</h4>
          {section.name === FormField.TRANSFUSION_ORDER && <p className={`${BLOCK}__hint`}>{TRANSFUSION_ORDER_HINT}</p>}
          {renderField(section.name, '1o1')}
        </div>
      ))}

      <div className={`${BLOCK}__section ${BLOCK}__section--divided`}>
        <h4 className={`${BLOCK}__section-title`}>{SectionTitle.PATIENT_INFORMATION}</h4>
        {PATIENT_INFORMATION_QUESTIONS.map((question) => (
          <ELSFlex key={question.name} left middle wrap className={`${BLOCK}__question-row`}>
            <ELSFlexItem w="1o2" md="1o1">
              <span className={`${BLOCK}__question-label`}>{question.label}</span>
            </ELSFlexItem>
            <ELSFlexItem w="1o2" md="1o1">
              <ErrorFormField
                className={`${BLOCK}__field`}
                formField={formFieldMap.get(question.name)}
                formSubmittedCount={formSubmittedCount}
                options={{ displayHorizontal: true }}
              />
            </ELSFlexItem>
          </ELSFlex>
        ))}
      </div>
    </>
  );

  const renderProductRequestTable = () => (
    <table className={`${BLOCK}__table`}>
      <thead>
        <tr>
          {PRODUCT_REQUEST_TABLE_HEADERS.map((header) => (
            <th key={header}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {productRequests.map((row) => (
          <tr key={row.id}>
            <td>{row.componentDesired}</td>
            <td>{row.numberOfUnits}</td>
            <td>{row.day}</td>
            <td>{row.time}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderProviderBloodProductRequest = () => (
    <>
      <h4 className={`${BLOCK}__section-title`}>Check as appropriate:</h4>
      {renderField(FormField.BLOOD_PRODUCT_MODIFICATION, '1o1')}
      <SectionDivider mt />
      <div className={`${BLOCK}__section`}>
        <ELSFlex left middle className={`${BLOCK}__section-header`}>
          <ELSFlexItem>
            <h4 className={`${BLOCK}__section-title`}>{SectionTitle.PRODUCT_REQUEST}</h4>
          </ELSFlexItem>
          <ELSFlexItem>
            {productRequests.length > 0 ? renderEditButton('Edit product request', onOpenProductRequestSidebar) : renderAddButton('Add', onOpenProductRequestSidebar)}
          </ELSFlexItem>
        </ELSFlex>
        {productRequests.length > 0 && (
          <>
            {renderProductRequestTable()}
            <Box p className={`${BLOCK}__info-box`}>
              <ELSFlex left middle>
                <ELSFlexItem className="u-els-margin-right">
                  <ELSIcon prefix="gizmo" name="information" size="1x" />
                </ELSFlexItem>
                <ELSFlexItem grow>{PRODUCT_REQUEST_NOTICE}</ELSFlexItem>
              </ELSFlex>
            </Box>
          </>
        )}
      </div>
      <SectionDivider mt />
    </>
  );

  const renderPreTransfusionVerification = () => (
    <>
      <p className={`${BLOCK}__notice ${BLOCK}__notice--bold`}>{VERIFICATION_INTRO}</p>
      {renderOrderedList(VERIFICATION_STEPS)}
      <SectionDivider mt />
      <div className={`${BLOCK}__section`}>
        <h4 className={`${BLOCK}__section-title`}>{SectionTitle.TRANSFUSION_STARTED_BY}</h4>
        {renderFieldRow([FormField.TRANSFUSION_STARTED_NAME, FormField.TRANSFUSION_STARTED_SIGNATURE], '1o2')}
      </div>
      <SectionDivider mt />
      <div className={`${BLOCK}__section`}>
        <h4 className={`${BLOCK}__section-title`}>{SectionTitle.DOUBLE_CHECK}</h4>
        {renderFieldRow([FormField.DOUBLE_CHECK_NAME, FormField.DOUBLE_CHECK_SIGNATURE], '1o2')}
      </div>
      <SectionDivider mt />
    </>
  );

  const renderVitalSignsTable = () => (
    <table className={`${BLOCK}__table`}>
      <thead>
        <tr>
          {VITAL_SIGNS_TABLE_HEADERS.map((header) => (
            <th key={header}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {transfusionDetails.flatMap((entry) =>
          entry.vitalSigns.map((vitalSign) => (
            <tr key={`${entry.id}-${vitalSign.vitalSign}`}>
              <td>{vitalSign.vitalSign}</td>
              <td>{vitalSign.beforeTransfusion}</td>
              <td>{vitalSign.fifteenMinutesAfter}</td>
              <td>{vitalSign.immediatelyAfter}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  const renderTransfusionAdministration = () => (
    <>
      <ELSFlex left middle className={`${BLOCK}__section-header`}>
        <ELSFlexItem>
          <h4 className={`${BLOCK}__section-title`}>{SectionTitle.ADMINISTRATION_DETAILS}</h4>
        </ELSFlexItem>
        <ELSFlexItem>
          {transfusionDetails.length > 0 ? renderEditButton('Edit administration details', onOpenTransfusionSidebar) : renderAddButton('Add', onOpenTransfusionSidebar)}
        </ELSFlexItem>
      </ELSFlex>
      {transfusionDetails.length > 0 && renderVitalSignsTable()}
      <SectionDivider mt />
      <div className={`${BLOCK}__section`}>
        <h4 className={`${BLOCK}__section-title`}>{SectionTitle.TRANSFUSION_DETAILS}</h4>
        {renderFieldRow([FormField.AMOUNT_TRANSFERRED, FormField.BLOOD_WARMER_TEMPERATURE_CELSIUS, FormField.BLOOD_WARMER_TEMPERATURE_FAHRENHEIT])}
      </div>
      <SectionDivider mt />
    </>
  );

  const renderPostTransfusionData = () => {
    const hasTransfusionReaction = String(formFieldMap.get(FormField.TRANSFUSION_REACTION)?.value ?? '') === TRANSFUSION_REACTION_YES;

    return (
      <>
        <h4 className={`${BLOCK}__section-title`}>Transfusion Reaction</h4>
        <ELSFlex left wrap>
          <ELSFlexItem w="1o3" md="1o1">
            <ErrorFormField className={`${BLOCK}__field`} formField={formFieldMap.get(FormField.TRANSFUSION_REACTION)} formSubmittedCount={formSubmittedCount} />
          </ELSFlexItem>
          {hasTransfusionReaction && (
            <ELSFlexItem w="2o3" md="1o1">
              {renderField(FormField.DESCRIBE_REACTION, '1o1')}
            </ELSFlexItem>
          )}
        </ELSFlex>
        <SectionDivider mt />
        <p className={`${BLOCK}__notice ${BLOCK}__notice--bold`}>{TRANSFUSION_REACTION_INTRO}</p>
        {renderOrderedList(TRANSFUSION_REACTION_STEPS)}
        <SectionDivider mt />
      </>
    );
  };

  const STEP_RENDERERS = [
    renderConsultationRequest,
    renderRationaleForTransfusion,
    renderProviderBloodProductRequest,
    renderPreTransfusionVerification,
    renderTransfusionAdministration,
    renderPostTransfusionData
  ];

  const isLastStep = activeStep === LAST_STEP_INDEX;

  return (
    <div className={BLOCK}>
      <ErrorCard chartMetaFormFields={formFieldMap as Map<string, ChartMetaFormField>} showErrorCard={formSubmittedCount > 0} />
      <h1>{ViewTitle.BLOOD_PRODUCT_ADMINISTRATION_RECORD}</h1>
      <SectionDivider mt />

      <div className={`${BLOCK}__flow`}>
        {STEP_CONFIGS.map((stepConfig, stepIndex) => {
          const isActive = stepIndex === activeStep;
          const canAccessStep = stepIndex <= maxSelectableStep;
          const isCompleted = Boolean(completedSteps[stepIndex]);

          return (
            <section className={`${BLOCK}__step`} key={stepConfig.title}>
              <button
                className={cx(`${BLOCK}__step-header`, canAccessStep ? `${BLOCK}__step-header--clickable` : `${BLOCK}__step-header--locked`)}
                disabled={!canAccessStep}
                onClick={() => onSelectStep(stepIndex)}
                type="button"
              >
                <span className={cx(`${BLOCK}__step-index`, { [`${BLOCK}__step-index--active`]: isActive, [`${BLOCK}__step-index--completed`]: isCompleted && !isActive })}>
                  {isCompleted && !isActive ? <ELSIcon name="checkmark" size="1x" color="white" title="Completed" /> : stepIndex + 1}
                </span>
                <span className={cx(`${BLOCK}__step-title`, { [`${BLOCK}__step-title--active`]: isActive })}>{stepConfig.title}</span>
              </button>

              {isActive && (
                <div className={`${BLOCK}__panel`}>
                  {STEP_RENDERERS[stepIndex]()}

                  <ChartPermission>
                    {({ allowSaveFragment }) => (
                      <div className={`${BLOCK}__actions`}>
                        {stepIndex > 0 && <ELSButton onClick={onBackStep} text="Back" type="secondary" iconName="chevron-left" iconPosition="left" iconSize="1x" />}
                        {isLastStep ? (
                          <>
                            <ELSButton
                              isDisabled={!isFinalStepReady || !allowSaveFragment}
                              onClick={onSaveClick}
                              text="Save & Complete"
                              type="primary"
                              iconName="chevron-right"
                              iconPosition="right"
                              iconSize="1x"
                            />
                            {/* Saving is what puts the fragment in the authoring store, which is what enables this. */}
                            {isAuthor && <ELSButton type="secondary" text="Return with data" isDisabled={!enableDisplayRecordsButton} onClick={displayAuthoringData} />}
                          </>
                        ) : (
                          <ELSButton
                            isDisabled={!canContinue}
                            onClick={onContinueStep}
                            text="Continue"
                            type="primary"
                            iconName="chevron-right"
                            iconPosition="right"
                            iconSize="1x"
                          />
                        )}
                        <ELSButton isDisabled={!canReset} onClick={onResetStep} text="Reset" type="tertiary" />
                      </div>
                    )}
                  </ChartPermission>
                </div>
              )}
            </section>
          );
        })}
      </div>

      <ProductRequestSidebar
        selectedNavId={NAV_ID.BLOOD_PRODUCT_ADMINISTRATION_RECORD}
        isSidebarOpen={isProductRequestSidebarOpen}
        hasSavedRows={productRequests.length > 0}
        resetSignal={productRequestResetCount}
        addProductRequests={onAddProductRequests}
        handleCloseSidebar={onCloseProductRequestSidebar}
      />
      <TransfusionDetailsSidebar
        selectedNavId={NAV_ID.BLOOD_PRODUCT_ADMINISTRATION_RECORD}
        isSidebarOpen={isTransfusionSidebarOpen}
        hasSavedEntry={transfusionDetails.length > 0}
        resetSignal={transfusionResetCount}
        addTransfusionDetail={onAddTransfusionDetail}
        handleCloseSidebar={onCloseTransfusionSidebar}
      />
    </div>
  );
};

export default BloodProductAdministrationRecordView;
